"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-3 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-3 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-3 w-28 bg-muted animate-pulse rounded-md" />
        <div className="h-3 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex h-9 items-center px-4 py-2 font-default text-[12px] font-medium uppercase tracking-[0.2em] text-foreground transition-colors duration-300 hover:text-primary hover:bg-transparent focus:bg-transparent">
                <span className="relative">
                  {item.label}
                  <span className="pointer-events-none absolute left-1/2 -bottom-2 h-1 w-1 -translate-x-1/2 rounded-lg bg-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-6">
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-56 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-48 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-44 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-8 px-6 pt-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onItemClick} className="font-default text-[32px] leading-tight tracking-tight text-foreground transition-colors duration-300 hover:text-primary">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Inpagenav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return;
}